import React from 'react';
import { OverlayWidget } from './api';

// ─── Types ─────────────────────────────────────────────────────────────────────
export type TemplateWidget = Omit<OverlayWidget, 'id' | 'sceneId' | 'createdAt' | 'updatedAt'>;

export interface TemplateScene {
  name: string;
  sortOrder: number;
  isDefault: boolean;
  widgets: TemplateWidget[];
}

export interface OverlayTemplate {
  id: string;
  name: string;
  description: string;
  tags: string[];
  scenes: TemplateScene[];
  renderPreview: () => React.ReactElement;
}

// ─── Shared widget factories ────────────────────────────────────────────────────

/**
 * Blue-bordered transparent camera-frame panel (center column only).
 * fillType:'none' renders as border-only so the live video shows through.
 */
const cameraPanel = (
  name: string, x: number, y: number, w: number, h: number, z: number,
): TemplateWidget => ({
  type: 'shape', name, x, y, width: w, height: h,
  rotation: 0, opacity: 1, zIndex: z, isVisible: true, isLocked: true, assetIds: [],
  properties: {
    shapeType: 'rectangle', fillType: 'none', fillColor: 'transparent',
    borderWidth: 3, borderColor: '#4a84b0', borderStyle: 'solid', borderRadius: 0,
  },
});

/**
 * Dark gradient bottom bar — full width, sits at the bottom of the frame.
 * Matches the production dark bar that houses the sheet label and clock.
 */
const bottomBar = (): TemplateWidget => ({
  type: 'shape', name: 'Bottom Bar', x: 0, y: 980, width: 1920, height: 100,
  rotation: 0, opacity: 1, zIndex: 2, isVisible: true, isLocked: true, assetIds: [],
  properties: {
    shapeType: 'rectangle', fillType: 'linear', fillAngle: 90, fillColor: '#111111',
    fillStops: [
      { color: '#000000', position: 0 },
      { color: '#1a1a1a', position: 100 },
    ],
    borderWidth: 0, borderRadius: 0, borderColor: '#000000', borderStyle: 'solid',
  },
});

/**
 * Large sheet identifier in the bottom-left of the bar.
 * Uses Impact for the classic broadcast look.
 */
const sheetLabel = (content = 'SHEET A'): TemplateWidget => ({
  type: 'text', name: 'Sheet Label', x: 20, y: 985, width: 500, height: 82,
  rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
  properties: {
    content, fontSize: 70, fontFamily: 'Impact, Arial Narrow, sans-serif', fontWeight: 400,
    color: '#ffffff', bgType: 'none', bgColor: 'transparent',
    textAlign: 'left', padding: 0, textTransform: 'uppercase',
    textDecoration: 'none', letterSpacing: 2, lineHeight: 1.1,
  },
});

/**
 * Date clock — bottom-right of bar, shows MM/ DD/ YYYY (matches production spacing).
 */
const bottomDateClock = (): TemplateWidget => ({
  type: 'clock', name: 'Date', x: 1540, y: 985, width: 370, height: 40,
  rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
  properties: {
    format: 'MM/ DD/ YYYY', fontSize: 28, fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 400,
    color: '#cccccc', bgType: 'none', bgColor: 'transparent', letterSpacing: 1,
    textAlign: 'right',
  },
});

/** Time clock — bottom-right of bar, below the date. */
const bottomTimeClock = (): TemplateWidget => ({
  type: 'clock', name: 'Time', x: 1540, y: 1030, width: 370, height: 46,
  rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
  properties: {
    format: 'HH:mm:ss', fontSize: 36, fontFamily: 'Roboto Mono, monospace', fontWeight: 400,
    color: '#ffffff', bgType: 'none', bgColor: 'transparent', letterSpacing: 2,
    textAlign: 'right',
  },
});

/** Network/broadcaster logos — top-left corner. */
const networkLogos = (): TemplateWidget => ({
  type: 'image', name: 'Network Logos', x: 5, y: 5, width: 250, height: 110,
  rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
  properties: { objectFit: 'contain' },
});

/** Blue accent bar to the left of the club badge (top-right corner). */
const badgeBarLeft = (): TemplateWidget => ({
  type: 'shape', name: 'Badge Bar Left', x: 1673, y: 5, width: 57, height: 110,
  rotation: 0, opacity: 1, zIndex: 9, isVisible: true, isLocked: true, assetIds: [],
  properties: {
    shapeType: 'rectangle', fillType: 'solid', fillColor: '#1565c0',
    borderWidth: 0, borderRadius: 0, borderColor: '#000000', borderStyle: 'solid',
  },
});

/** Club badge / logo — top-right corner. */
const clubBadge = (): TemplateWidget => ({
  type: 'image', name: 'Club Badge', x: 1730, y: 5, width: 143, height: 110,
  rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
  properties: { objectFit: 'contain' },
});

/** Blue accent bar to the right of the club badge (top-right corner). */
const badgeBarRight = (): TemplateWidget => ({
  type: 'shape', name: 'Badge Bar Right', x: 1873, y: 5, width: 47, height: 110,
  rotation: 0, opacity: 1, zIndex: 9, isVisible: true, isLocked: true, assetIds: [],
  properties: {
    shapeType: 'rectangle', fillType: 'solid', fillColor: '#1565c0',
    borderWidth: 0, borderRadius: 0, borderColor: '#000000', borderStyle: 'solid',
  },
});

/**
 * Sponsor slideshow — fills the center banner slot between the two center panels.
 * User adds sponsor/ad images as assets after applying the template.
 */
const sponsorBanner = (): TemplateWidget => ({
  type: 'slideshow', name: 'Sponsor Banner', x: 720, y: 470, width: 480, height: 90,
  rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
  properties: { objectFit: 'contain', transitionType: 'fade' },
});

/**
 * Full-screen navy background — used in non-live scenes that replace the video feed.
 */
const navyBackground = (): TemplateWidget => ({
  type: 'shape', name: 'Background', x: 0, y: 0, width: 1920, height: 1080,
  rotation: 0, opacity: 1, zIndex: 1, isVisible: true, isLocked: true, assetIds: [],
  properties: {
    shapeType: 'rectangle', fillType: 'linear', fillAngle: 135, fillColor: '#012249',
    fillStops: [
      { color: '#012249', position: 0 },
      { color: '#1a3a5c', position: 50 },
      { color: '#012249', position: 100 },
    ],
    borderWidth: 0, borderRadius: 0, borderColor: '#000000', borderStyle: 'solid',
  },
});

/** Common corner decorations shared across all scenes. */
const cornerElements = (): TemplateWidget[] => [
  networkLogos(),
  badgeBarLeft(),
  clubBadge(),
  badgeBarRight(),
  bottomBar(),
  sheetLabel(),
  bottomDateClock(),
  bottomTimeClock(),
];

// ─── Curling Broadcast Template ─────────────────────────────────────────────────
/**
 * Matches the Four Seasons Curling Center production overlay layout:
 *
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ [SSN logos]            center col           [blue|badge|blue]    │
 * │                   ┌────────────────┐                             │
 * │                   │  center-top    │                             │
 * │   (transparent)   │   camera       │        (transparent)        │
 * │                   ├────────────────┤                             │
 * │                   │  SPONSOR LOGO  │                             │
 * │                   ├────────────────┤                             │
 * │                   │  center-bottom │                             │
 * │                   │   camera       │                             │
 * │                   └────────────────┘                             │
 * ├──────────────────────────────────────────────────────────────────┤
 * │ SHEET A                                          03/16/2026      │
 * │                                                  06:32:29        │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * Live scene: fully transparent except center column frames and bottom bar.
 * Left and right camera areas are open (no border) — video shows through.
 * Center column: x=720, w=480 (centered in 1920px frame).
 */
const CURLING_BROADCAST: OverlayTemplate = {
  id: 'curling-broadcast',
  name: 'Curling Broadcast',
  description: 'Matches the FSCC production overlay. Center column with two framed camera panels and sponsor banner. Transparent sides. Dark bottom bar with sheet label and clock.',
  tags: ['curling', 'broadcast', 'camera-frame', 'keyed'],

  scenes: [
    // ── Scene 1: Live ─────────────────────────────────────────────────────────
    // Transparent background — keyed over live video in OBS/vMix.
    // Only the center column panels have blue borders.
    // x=720, w=480 centers the column in the 1920px frame.
    {
      name: 'Live',
      sortOrder: 0,
      isDefault: true,
      widgets: [
        cameraPanel('Center Top Panel',    720,   0, 480, 470, 3),
        cameraPanel('Center Bottom Panel', 720, 560, 480, 420, 3),
        sponsorBanner(),
        ...cornerElements(),
      ],
    },

    // ── Scene 2: Pre-Game ─────────────────────────────────────────────────────
    {
      name: 'Pre-Game',
      sortOrder: 1,
      isDefault: false,
      widgets: [
        navyBackground(),
        {
          type: 'image', name: 'Club Badge (Center)', x: 860, y: 200, width: 200, height: 200,
          rotation: 0, opacity: 0.9, zIndex: 5, isVisible: true, isLocked: false, assetIds: [],
          properties: { objectFit: 'contain' },
        },
        {
          type: 'text', name: 'Pre-Game Label', x: 460, y: 430, width: 1000, height: 160,
          rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
          properties: {
            content: 'PRE-GAME', fontSize: 100, fontFamily: 'Impact, Arial Narrow, sans-serif',
            fontWeight: 400, color: '#ffffff', bgType: 'none', bgColor: 'transparent',
            textAlign: 'center', padding: 0, textTransform: 'uppercase',
            textDecoration: 'none', letterSpacing: 10, lineHeight: 1.1,
            textShadowEnabled: true, textShadowX: 0, textShadowY: 4,
            textShadowBlur: 32, textShadowColor: 'rgba(0,0,0,0.6)',
          },
        },
        {
          type: 'text', name: 'Subtitle', x: 660, y: 610, width: 600, height: 70,
          rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
          properties: {
            content: 'GAME 1 · SHEET A', fontSize: 34, fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 400, color: '#a0c4e8', bgType: 'none', bgColor: 'transparent',
            textAlign: 'center', padding: 0, textTransform: 'uppercase',
            textDecoration: 'none', letterSpacing: 4, lineHeight: 1.2,
          },
        },
        ...cornerElements(),
      ],
    },

    // ── Scene 3: Starting Soon ────────────────────────────────────────────────
    {
      name: 'Starting Soon',
      sortOrder: 2,
      isDefault: false,
      widgets: [
        navyBackground(),
        {
          type: 'image', name: 'Club Badge (Center)', x: 860, y: 180, width: 200, height: 200,
          rotation: 0, opacity: 0.9, zIndex: 5, isVisible: true, isLocked: false, assetIds: [],
          properties: { objectFit: 'contain' },
        },
        {
          type: 'text', name: 'Starting Soon Label', x: 360, y: 400, width: 1200, height: 150,
          rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
          properties: {
            content: 'STARTING SOON', fontSize: 92, fontFamily: 'Impact, Arial Narrow, sans-serif',
            fontWeight: 400, color: '#ffffff', bgType: 'none', bgColor: 'transparent',
            textAlign: 'center', padding: 0, textTransform: 'uppercase',
            textDecoration: 'none', letterSpacing: 8, lineHeight: 1.1,
            textShadowEnabled: true, textShadowX: 0, textShadowY: 4,
            textShadowBlur: 32, textShadowColor: 'rgba(0,0,0,0.6)',
          },
        },
        {
          type: 'shape', name: 'Accent Line', x: 760, y: 565, width: 400, height: 4,
          rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
          properties: {
            shapeType: 'rectangle', fillType: 'linear', fillAngle: 90, fillColor: '#4a84b0',
            fillStops: [
              { color: 'transparent', position: 0 },
              { color: '#4a84b0', position: 30 },
              { color: '#4a84b0', position: 70 },
              { color: 'transparent', position: 100 },
            ],
            borderWidth: 0, borderRadius: 0, borderColor: '#000000', borderStyle: 'solid',
          },
        },
        {
          type: 'text', name: 'Teams', x: 460, y: 590, width: 1000, height: 80,
          rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
          properties: {
            content: 'Team 1  vs  Team 2', fontSize: 46, fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 400, color: '#d0e8ff', bgType: 'none', bgColor: 'transparent',
            textAlign: 'center', padding: 0, textTransform: 'none',
            textDecoration: 'none', letterSpacing: 2, lineHeight: 1.2,
          },
        },
        {
          type: 'clock', name: 'Current Time', x: 760, y: 695, width: 400, height: 90,
          rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
          properties: {
            format: 'HH:mm', fontSize: 64, fontFamily: 'Roboto Mono, monospace',
            fontWeight: 300, color: '#d0e8ff', bgType: 'none', bgColor: 'transparent', letterSpacing: 4,
          },
        },
        ...cornerElements(),
      ],
    },

    // ── Scene 4: Game Over ────────────────────────────────────────────────────
    {
      name: 'Game Over',
      sortOrder: 3,
      isDefault: false,
      widgets: [
        navyBackground(),
        {
          type: 'image', name: 'Club Badge (Center)', x: 860, y: 160, width: 200, height: 200,
          rotation: 0, opacity: 0.9, zIndex: 5, isVisible: true, isLocked: false, assetIds: [],
          properties: { objectFit: 'contain' },
        },
        {
          type: 'text', name: 'Game Over Label', x: 560, y: 390, width: 800, height: 110,
          rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
          properties: {
            content: 'FINAL SCORE', fontSize: 72, fontFamily: 'Impact, Arial Narrow, sans-serif',
            fontWeight: 400, color: '#d0e8ff', bgType: 'none', bgColor: 'transparent',
            textAlign: 'center', padding: 0, textTransform: 'uppercase',
            textDecoration: 'none', letterSpacing: 8, lineHeight: 1.1,
          },
        },
        {
          type: 'shape', name: 'Score Divider', x: 560, y: 512, width: 800, height: 3,
          rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
          properties: {
            shapeType: 'rectangle', fillType: 'solid', fillColor: '#4a84b0',
            borderWidth: 0, borderRadius: 0, borderColor: '#000000', borderStyle: 'solid',
          },
        },
        {
          type: 'scoreboard', name: 'Final Scoreboard', x: 210, y: 535, width: 1500, height: 180,
          rotation: 0, opacity: 1, zIndex: 10, isVisible: true, isLocked: false, assetIds: [],
          properties: {
            team1: 'Team 1', score: '8 - 6', team2: 'Team 2',
            fontSize: 86, fontFamily: 'Roboto, Arial, sans-serif',
            colorTeam1: '#ffffff', colorTeam2: '#ffffff', colorScore: '#d0e8ff',
            bgType: 'none', bgColor: 'transparent',
            showHammer: false, hammerTeam: '1',
          },
        },
        ...cornerElements(),
      ],
    },
  ],

  // ── SVG Preview ───────────────────────────────────────────────────────────────
  // Matches the production layout: transparent sides, center column with blue
  // bordered panels, sponsor banner, dark bottom bar, corner logos.
  renderPreview: () =>
    React.createElement('svg', {
      viewBox: '0 0 1920 1080',
      xmlns: 'http://www.w3.org/2000/svg',
      style: { width: '100%', height: '100%' },
    },
      React.createElement('defs', null,
        // Checkerboard indicates transparency on the sides
        React.createElement('pattern', {
          id: 'tmpl-checker', x: 0, y: 0, width: 40, height: 40,
          patternUnits: 'userSpaceOnUse',
        },
          React.createElement('rect', { width: 40, height: 40, fill: '#18243a' }),
          React.createElement('rect', { width: 20, height: 20, fill: '#1e2d45' }),
          React.createElement('rect', { x: 20, y: 20, width: 20, height: 20, fill: '#1e2d45' }),
        ),
      ),

      // Transparent background (checkerboard)
      React.createElement('rect', { width: 1920, height: 1080, fill: 'url(#tmpl-checker)' }),

      // ── Center column panels (x=720, w=480) ─────────────────────────────────
      // Center-top panel
      React.createElement('rect', { x: 720, y: 0,   width: 480, height: 470, fill: 'none', stroke: '#4a84b0', strokeWidth: 6 }),
      // Sponsor banner
      React.createElement('rect', { x: 720, y: 470, width: 480, height: 90,  fill: '#0d1f35', opacity: 0.9 }),
      React.createElement('text', { x: 960, y: 522, textAnchor: 'middle', fill: '#4a84b0', fontSize: 32, fontFamily: 'sans-serif', opacity: 0.7 }, 'SPONSOR'),
      // Center-bottom panel
      React.createElement('rect', { x: 720, y: 560, width: 480, height: 420, fill: 'none', stroke: '#4a84b0', strokeWidth: 6 }),

      // ── Bottom bar ───────────────────────────────────────────────────────────
      React.createElement('rect', { x: 0, y: 980, width: 1920, height: 100, fill: '#111111' }),

      // Sheet label
      React.createElement('text', {
        x: 24, y: 1062, fill: '#ffffff', fontSize: 64,
        fontFamily: 'Impact, sans-serif', letterSpacing: 2,
      }, 'SHEET A'),

      // Date / time placeholders
      React.createElement('text', { x: 1900, y: 1022, textAnchor: 'end', fill: '#cccccc', fontSize: 26, fontFamily: 'sans-serif' }, '03/ 16/ 2026'),
      React.createElement('text', { x: 1900, y: 1068, textAnchor: 'end', fill: '#ffffff',  fontSize: 34, fontFamily: 'monospace', letterSpacing: 2 }, '06:32:29'),

      // ── Top-left network logos placeholder ──────────────────────────────────
      React.createElement('rect', { x: 5, y: 5, width: 250, height: 110, rx: 4, fill: '#0d1f35', opacity: 0.8 }),
      React.createElement('text', { x: 130, y: 68, textAnchor: 'middle', fill: '#4a84b0', fontSize: 28, fontFamily: 'sans-serif', opacity: 0.8 }, 'NETWORK'),

      // ── Top-right: blue bar | club badge | blue bar ──────────────────────────
      React.createElement('rect', { x: 1673, y: 5, width: 57,  height: 110, fill: '#1565c0' }),
      React.createElement('rect', { x: 1730, y: 5, width: 143, height: 110, fill: '#0d1f35', opacity: 0.9 }),
      React.createElement('circle', { cx: 1801, cy: 60, r: 36, fill: 'none', stroke: '#4a84b0', strokeWidth: 6 }),
      React.createElement('circle', { cx: 1801, cy: 60, r: 16, fill: 'none', stroke: '#6ab0e0', strokeWidth: 5 }),
      React.createElement('circle', { cx: 1801, cy: 60, r: 5,  fill: '#8ecef0' }),
      React.createElement('rect', { x: 1873, y: 5, width: 47,  height: 110, fill: '#1565c0' }),
    ),
};

export const OVERLAY_TEMPLATES: OverlayTemplate[] = [
  CURLING_BROADCAST,
];
